// Configuración centralizada de permisos y rutas.
// Debe estar alineada con el backend (ver docs/PERMISOS-Y-ROLES.md).

export const ROLES = {
  DUEÑO: 'DUEÑO',
  PET_SITTER: 'PET_SITTER',
  VETERINARIO: 'VETERINARIO',
  ADMIN: 'ADMIN',
  SUPERADMIN: 'SUPERADMIN',
}

// Mapa de rutas protegidas → roles permitidos.
// Se usa como base para canAccessRoute y para mantener
// la lógica en un solo lugar.
const ROUTE_RULES = [
  { pattern: /^\/usuarios(\/|$)/, roles: [ROLES.SUPERADMIN] },
  { pattern: /^\/clinicas(\/|$)/, roles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.VETERINARIO] },
  { pattern: /^\/veterinarios(\/|$)/, roles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.VETERINARIO] },
  { pattern: /^\/reportes(\/|$)/, roles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.VETERINARIO] },
  { pattern: /^\/configuracion(\/|$)/, roles: [ROLES.SUPERADMIN] },
  { pattern: /^\/suscripciones-pendientes(\/|$)/, roles: [ROLES.VETERINARIO] },
  { pattern: /^\/suscripciones-clinicas(\/|$)/, roles: [ROLES.DUEÑO] },
]

export function canAccessRoute(path, role) {
  const normalizedPath = String(path || '/').split('?')[0] || '/'
  const rule = ROUTE_RULES.find((r) => r.pattern.test(normalizedPath))

  // Rutas sin regla explícita (/, /citas, /mascotas, /perfil, etc.) siempre accesibles.
  // Antes: if (!role) return false → ocultaba Inicio en bottom nav al hidratar sesión.
  if (!rule) {
    return true
  }

  if (!role) return false
  return rule.roles.includes(role)
}

// Control específico para mostrar el item de Reportes en la navegación.
export function canSeeReportesInNav(role) {
  return (
    role === ROLES.ADMIN ||
    role === ROLES.SUPERADMIN ||
    role === ROLES.VETERINARIO
  )
}

// Funciones de capacidades por rol. Mantener simples y puras
// para poder reutilizarlas en componentes y hooks.
export const CAPABILITIES = {
  canAccessReportes: (role) =>
    role === ROLES.ADMIN || role === ROLES.SUPERADMIN || role === ROLES.VETERINARIO,

  canAccessUsuarios: (role) => role === ROLES.SUPERADMIN,

  canAccessConfiguracion: (role) => role === ROLES.SUPERADMIN,

  canCreateFicha: (role) =>
    role === ROLES.VETERINARIO || role === ROLES.ADMIN || role === ROLES.SUPERADMIN,

  canAccessSuscripcionesClinicas: (role) => role === ROLES.DUEÑO,

  canAccessClinicas: (role) =>
    role === ROLES.ADMIN || role === ROLES.SUPERADMIN || role === ROLES.VETERINARIO,

  canAccessVeterinarios: (role) =>
    role === ROLES.ADMIN || role === ROLES.SUPERADMIN || role === ROLES.VETERINARIO,

  canAccessSuscripcionesPendientes: (role, isVeterinarioAdmin = false) =>
    role === ROLES.VETERINARIO && isVeterinarioAdmin,

  requiresSuperAdmin: (role) => role === ROLES.SUPERADMIN,

  requiresAdmin: (role) => role === ROLES.ADMIN || role === ROLES.SUPERADMIN,

  // Acciones sobre clínica y veterinarios (veterinario admin de clínica o roles altos)
  canEditClinica: (role, isVeterinarioAdmin) =>
    role === ROLES.ADMIN || role === ROLES.SUPERADMIN || isVeterinarioAdmin,

  canCreateClinica: (role, isVeterinarioAdmin, tieneClinica) => {
    if (role === ROLES.ADMIN || role === ROLES.SUPERADMIN) return true
    // Un veterinario admin solo puede crear clínica si aún no tiene una asignada
    if (role === ROLES.VETERINARIO && isVeterinarioAdmin && !tieneClinica) return true
    return false
  },

  canCreateVeterinario: (role, isVeterinarioAdmin) =>
    role === ROLES.ADMIN || role === ROLES.SUPERADMIN || isVeterinarioAdmin,

  canManageVeterinario: (role, isVeterinarioAdmin) =>
    role === ROLES.ADMIN || role === ROLES.SUPERADMIN || isVeterinarioAdmin,

  // Mascotas y citas: por ahora solo controlamos visibilidad básica,
  // el backend es la fuente de verdad final.
  canCreateMascota: (role) => role === ROLES.DUEÑO || role === ROLES.VETERINARIO,

  canAprobarCita: (role) =>
    role === ROLES.VETERINARIO || role === ROLES.ADMIN || role === ROLES.SUPERADMIN,

  canAsignarFechaCita: (role) =>
    role === ROLES.VETERINARIO || role === ROLES.ADMIN || role === ROLES.SUPERADMIN,

  canCompletarCita: (role) =>
    role === ROLES.VETERINARIO || role === ROLES.ADMIN || role === ROLES.SUPERADMIN,

  canRechazarCita: (role) =>
    role === ROLES.VETERINARIO || role === ROLES.ADMIN || role === ROLES.SUPERADMIN,
}

